import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Align, Collision, PopupModule } from '@progress/kendo-angular-popup';
import { FormButtonStates } from '../../classes/form-button.states';
import { IconComponent } from '../../../../../shared/components/ui/icon/icon.component';
import { TranslatePipe } from '../../../../../shared/pipes/translate/translate.pipe';
import { ToggleArrowComponent } from '../../../../../shared/components/ui/toggle-arrow/toggle-arrow.component';
import { KendoPopupContains } from '../../../../../shared/functions/kendo/kendo-popup-contains';
import { GridService } from '../../../widgets/collection-list-widget/services/grid/grid.service';
import { CollectionForm } from 'src/models/ts/collection-form.model';
import { LinkingService } from 'src/app/core/services/linking/linking.service';
import { GridContextPermissions } from '../../../widgets/collection-list-widget/classes/grid-context-permissions';
import {
  GridContextPermissionsMetadata,
} from '../../../widgets/collection-list-widget/interfaces/grid-context-permissions-metadata';
import { userSettingsFeature } from 'src/app/store/features/user-settings/user-settings-feature';
import { UserType } from 'src/models/ts/user-type.model';
import { Store } from '@ngrx/store';
import { ListDesignGridOptionsDto } from 'src/models/ts/list-design-grid-options-dto.model';
import { CollectionListApiService } from 'src/app/api/bizzmine/collection-list/collection-list-api.service';
import { take } from 'rxjs';
import { selectByCollectionId } from 'src/app/store/features/collections/collections-selectors';
import { CollectionType } from 'src/models/ts/collection-type.model';
import { TaskService } from 'src/app/shared/services/tasks/task.service';
import { workspaceSidebarFeature } from 'src/app/store/features/workspace-sidebar/workspace-sidebar-feature';
import { StoreCollectionForm } from '../../../../../store/features/forms/forms-state';
import { FormGroup } from '@angular/forms';
import { SidebarService } from '../../../../../shared/services/sidebar/sidebar.service';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PwaService } from 'src/app/core/services/pwa/pwa.service';
import { KENDO_MENU, MenuItemComponent, MenuSelectEvent } from '@progress/kendo-angular-menu';
import { MenuAction } from 'src/app/shared/enums/menu.action';
import { RetrDocAccessType } from 'src/models/ts/retr-doc-access-type.model';

/**
 * Displays the header/top navigation of a form.
 * Emits events when the user clicks on the save or cancel button.
 */
@Component({
  selector: 'bizz-form-header',
  standalone: true,
  imports: [CommonModule, IconComponent, TranslatePipe, PopupModule, ToggleArrowComponent, MenuItemComponent, KENDO_MENU],
  templateUrl: './form-header.component.html',
  styleUrls: ['./form-header.component.scss'],
})
export class FormHeaderComponent implements OnInit {
  @Input() public readMode: boolean;
  @Input({ required: true }) public buttonStates: FormButtonStates;
  @Input() public dataItem: StoreCollectionForm;
  @Input() public formGroup: FormGroup;
  @Output() public canceled = new EventEmitter<void>();
  @Output() public submitted = new EventEmitter<{ closeStep: boolean, closeForm: boolean }>();
  @Output() public editClicked = new EventEmitter<void>();
  public showPropertiesPopup = false;
  public showSavePopup = false;
  public popupCollision: Collision = { horizontal: 'flip', vertical: 'fit' };
  public popupAnchorAlignProperties: Align = { horizontal: 'left', vertical: 'bottom' };
  public popupAnchorAlignSave: Align = { horizontal: 'right', vertical: 'bottom' };
  public popupAlign: Align = { horizontal: 'right', vertical: 'top' };
  public readonly UserType = UserType;
  public MenuAction = MenuAction;
  public CollectionType = CollectionType;

  @ViewChild('saveAnchor', { static: true }) public saveAnchor: ElementRef;
  @ViewChild('savePopup', { static: true }) public savePopup: ElementRef;

  @ViewChild('propertiesAnchor', { static: true }) public propertiesAnchor: ElementRef;
  @ViewChild('propertiesPopup', { static: true }) public propertiesPopup: ElementRef;
  private workspaceIdSignal = this.store$.selectSignal(workspaceSidebarFeature.selectID);
  private permissions: GridContextPermissions;
  private metaData: GridContextPermissionsMetadata;

  public constructor(private gridService: GridService,
                     private linkingService: LinkingService,
                     private collectionListApiService: CollectionListApiService,
                     private sidebarService: SidebarService,
                     private store$: Store, private taskService: TaskService,
                     private documentEventsService: DocumentEventsService,
                     private ngZone: NgZone,
                     private destroyRef: DestroyRef,
                     public pwaService: PwaService,
  ) {
  }

  public get userType(): UserType {
    return this.store$.selectSignal(userSettingsFeature.selectUserType)();
  }

  public get userId(): UserType {
    return this.store$.selectSignal(userSettingsFeature.selectUserID)();
  }

  public get form(): CollectionForm {
    return this.dataItem.data;
  }

  public ngOnInit(): void {

    this.store$.select(selectByCollectionId(this.form.CollectionsID)).subscribe((res) => {

      this.metaData = {
        userType: this.userType,
        userId: this.userId,
        trashFilterActive: false,
        isDocumentTreeGrid: false,
        listOptions: res?.data.ListOptions as ListDesignGridOptionsDto,
        collectionType: res?.data.CollectionsType as CollectionType,
        selectedTreeNode: null,
        managerTrainingQueue: false,
      };
    });

    this.documentEventsService.documentClickEvent.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (event: Event) => {
          if (event?.target && this.checkHandleClick(event.target)) {
            this.ngZone.run(() => {
              this.onDocumentClick(event);
            });
          }
        });
  }

  public checkHandleClick(target: EventTarget): boolean {
    return this.showSavePopup && !KendoPopupContains(target, this.saveAnchor, this.savePopup) ||
      this.showPropertiesPopup && !KendoPopupContains(target, this.propertiesAnchor, this.propertiesPopup);
  }

  /**
   * Closes the popup if the user clicks outside the popup or anchor.
   * @param event
   */
  public onDocumentClick(event: Event): void {
    this.toggleSavePopup(undefined, false);
    this.togglePropertiesPopup(undefined, false);
  }

  public copyLinkToRecord(): void {
    this.linkingService.copyLinkToRecord(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID, this.form.GuidCheckSum);
  }

  public openPermissions(): void {
    this.gridService.openPermissionsPage(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID, this.form.FoldersID, this.form.InheritType, this.form.DocumentProperties.Permissions, this.dataItem.id, true);

  }

  public openDocumentSettings(): void {
    this.gridService.openDocumentSettings(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID);
  }

  public openFlowStatus(): void {
    this.gridService.openFlowStatusPage(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID, this.form.MethodType, this.workspaceIdSignal(), true);
  }

  public openHistory(): void {
    this.gridService.openHistoryPage(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID);
  }

  public openTrackChanges(): void {
    this.gridService.openTrackchangesPage(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID);
  }

  public openMonitor(): void {
    this.gridService.openMonitorModal(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID);
  }

  public openArchive(): void {
    this.collectionListApiService.getCollectionInstancefromList(this.form.CollectionsID, this.form.InstancesID)
      .pipe(take(1))
      .subscribe((data) => {
        this.permissions = new GridContextPermissions();
        this.permissions.setMetaData(this.metaData);
        this.permissions.loadMenuPermissions(data);

        this.gridService.openArchiveModal(structuredClone(data), this.permissions);
      });
  }

  public openReport(): void {
    this.gridService.openReportModal(this.form.CollectionsID, this.form.InstancesID, this.form.VersionsID, this.form.ReportSettings, this.form.GuidCheckSum);
  }

  public forwardTask(): void {
    this.taskService.forwardTask({
      taskId: this.form.TasksID,
      collectionId: this.form.CollectionsID,
      assignedToId: this.form.AssignedToID,
    });
  }

  public submit(closeStep: boolean, closeForm: boolean): void {
    this.submitted.emit({ closeStep, closeForm });
  }

  public cancel(): void {
    this.canceled.emit();
  }

  public togglePropertiesPopup(event?: MouseEvent, value?: boolean): void {
    this.showPropertiesPopup = value != undefined ? value : !this.showPropertiesPopup;
    if (event)
      event.stopImmediatePropagation();
  }

  public toggleSavePopup(event?: MouseEvent, value?: boolean): void {
    this.showSavePopup = value != undefined ? value : !this.showSavePopup;
    if (event)
      event.stopImmediatePropagation();
  }

  public onPropertiesMenuItemClicked($event: MenuSelectEvent): void {
    // Kendo menu select event items store the custom object under item.data
    const data = $event?.item?.data as { action?: MenuAction } | undefined;
    const action = data?.action;
    if (action == null) {
      return; // clicked an item without an action (e.g. group header or separator)
    }
    switch (action) {
      case MenuAction.CopyLinkToRecord:
        this.copyLinkToRecord();
        break;
      case MenuAction.DownloadQrCodeToRecord:
        this.gridService.downloadQRCodeToRecord({
          CollectionsID: this.form.CollectionsID,
          ID: this.form.InstancesID,
          VersionsID: this.form.VersionsID,
        });
        break;
      case MenuAction.DownloadQRCodeDirectLink:
        this.gridService.downloadQRCodeDirectLink({
          CollectionsID: this.form.CollectionsID,
          ID: this.form.InstancesID,
          VersionsID: this.form.VersionsID,
          RetrDocAccessType: this.form.RetrDocAccessType,
        });
        break;
      case MenuAction.CopyDirectLink:
        this.gridService.copyDirectLink({
          CollectionsID: this.form.CollectionsID,
          ID: this.form.InstancesID,
          VersionsID: this.form.VersionsID,
          RetrDocAccessType: this.form.RetrDocAccessType,
        });
        break;
      case MenuAction.Permissions:
        this.openPermissions();
        break;
      case MenuAction.DocumentSettings:
        this.openDocumentSettings();
        break;
      case MenuAction.FlowStatus:
        this.openFlowStatus();
        break;
      case MenuAction.Monitor:
        this.openMonitor();
        break;
      case MenuAction.History:
        this.openHistory();
        break;
      case MenuAction.TrackChanges:
        this.openTrackChanges();
        break;
      case MenuAction.Archive:
        this.openArchive();
        break;
      case MenuAction.Report:
        this.openReport();
        break;
      default:
        console.warn('Unhandled menu action', action);
        break;
    }
  }
}
